<script setup>
import { computed, nextTick, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const searchQuery = ref('');
const searchInput = ref(null);

const filteredAccounts = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return sortedCurrentUserAccounts.value;
  return sortedCurrentUserAccounts.value.filter(account =>
    account.name.toLowerCase().includes(query)
  );
});

const handleTrigger = (toggle, wasOpen) => {
  if (!showAccountSwitcher.value) return;
  toggle();
  if (wasOpen) {
    searchQuery.value = '';
  } else {
    nextTick(() => searchInput.value?.focus());
  }
};

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle, isOpen }">
      <button
        id="navbar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-lg px-1.5 py-1"
        :class="[
          isOpen && 'bg-n-alpha-1',
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="handleTrigger(toggle, isOpen)"
      >
        <Avatar :name="currentAccount.name" :size="28" class="flex-shrink-0" />
        <span
          class="text-sm font-medium leading-5 text-n-slate-12 truncate flex-1 text-left"
          aria-live="polite"
        >
          {{ currentAccount.name }}
        </span>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-lucide-chevron-down size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody v-if="showAccountSwitcher" class="min-w-80 z-50">
      <div class="px-2 pb-2">
        <div
          class="flex items-center gap-2 px-2 rounded-lg h-8 ring-1 ring-n-weak/60 bg-n-alpha-1 transition-shadow focus-within:ring-n-slate-7"
        >
          <span
            class="flex-shrink-0 i-lucide-search size-3.5 text-n-slate-11"
          />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')"
            class="!h-7 !mb-0 !p-0 !bg-transparent !outline-none hover:!outline-none focus:!outline-none flex-1 text-sm text-n-slate-12 placeholder:text-n-slate-10 border-none"
            @keydown.escape.stop="searchQuery = ''"
          />
        </div>
      </div>
      <DropdownSection
        v-if="filteredAccounts.length"
        :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')"
      >
        <DropdownItem
          v-for="account in filteredAccounts"
          :id="`account-${account.id}`"
          :key="account.id"
          class="cursor-pointer"
          @click="onChangeAccount(account.id)"
        >
          <template #label>
            <div
              :for="account.name"
              class="text-left rtl:text-right flex gap-2 items-center"
            >
              <span
                class="text-n-slate-12 max-w-36 truncate min-w-0"
                :title="account.name"
              >
                {{ account.name }}
              </span>
              <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
              <span
                class="text-n-slate-11 max-w-24 truncate capitalize"
                :title="account.name"
              >
                {{
                  account.custom_role_id
                    ? account.custom_role.name
                    : account.role
                }}
              </span>
            </div>
            <Icon
              v-show="account.id === accountId"
              icon="i-lucide-check"
              class="text-n-teal-11 size-5"
            />
          </template>
        </DropdownItem>
      </DropdownSection>
      <div v-else class="px-4 py-3 text-sm text-center text-n-slate-11">
        {{ t('CONVERSATION.CONVERSATION_PARTICIPANTS.NO_RECORDS_FOUND') }}
      </div>
      <DropdownItem v-if="globalConfig.createNewAccountFromDashboard">
        <ButtonNext
          color="slate"
          variant="faded"
          class="w-full"
          size="sm"
          @click="emitNewAccount"
        >
          {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
        </ButtonNext>
      </DropdownItem>
    </DropdownBody>
  </DropdownContainer>
</template>
